import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Crown, BarChart3, HardDrive } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export default function SubscriptionCard() {
  const { profile } = useAuth();
  const [planLimits, setPlanLimits] = React.useState<{ api_calls_limit: number; monthly_space_limit: number } | null>(null);

  React.useEffect(() => {
    const fetchPlanLimits = async () => {
      if (!profile) return;
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('api_calls_limit, monthly_space_limit')
        .eq('name', profile.subscription_plan)
        .single();
      if (!error && data) {
        setPlanLimits({
          api_calls_limit: data.api_calls_limit,
          monthly_space_limit: data.monthly_space_limit,
        });
      }
    };
    fetchPlanLimits();
  }, [profile]);

  if (!profile || !planLimits) return null;

  const apiUsagePercent = (profile.api_calls_used / planLimits.api_calls_limit) * 100;
  const storageUsedMB = (profile.monthly_space_used || 0) / 1024 / 1024;
  const storageUsagePercent = (storageUsedMB / (planLimits.monthly_space_limit / 1024 / 1024)) * 100;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Crown className="h-5 w-5 text-purple-600" />
            <span>Subscription Details</span>
          </div>
          <Badge className="bg-gray-500 text-white">
            {profile.subscription_plan.charAt(0).toUpperCase() + profile.subscription_plan.slice(1)} Plan
          </Badge>
        </CardTitle>
        <CardDescription>
          Your current plan usage and limits
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium">API Calls</span>
            </div>
            <span className="text-sm text-gray-600">
              {profile.api_calls_used} / {planLimits.api_calls_limit}
            </span>
          </div>
          <Progress value={apiUsagePercent} className="h-2" />
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <HardDrive className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium">Storage Used</span>
            </div>
            <span className="text-sm text-gray-600">
              {storageUsedMB.toFixed(1)} MB / {(planLimits.monthly_space_limit / 1024 / 1024) >= 1024 ? `${(planLimits.monthly_space_limit / 1024 / 1024 / 1024).toFixed(1)} GB` : `${(planLimits.monthly_space_limit / 1024 / 1024).toFixed(1)} MB`}
            </span>
          </div>
          <Progress value={storageUsagePercent} className="h-2" />
        </div>

        {profile.subscription_plan === 'free' && (
          <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
            <h4 className="font-medium text-purple-900 mb-2">Upgrade Your Plan</h4>
            <p className="text-sm text-purple-700 mb-3">
              Get more API calls, storage, and premium features with our Pro and Premium plans.
            </p>
            <Button asChild>
              <Link to="/pricing">View Plans & Upgrade</Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
